import { useNavigate } from "react-router-dom";
import { ReceiptText, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useMySubscriptions } from "@/hooks/useSubscription";
import { usePayments } from "@/hooks/usePayments";
import { formatRupees, formatDate } from "@/lib/format";
import type {
  PaymentRecord,
  PaymentStatus,
  SubscriptionRecord,
  SubscriptionStatus,
} from "@/lib/api-types";

const SUB_STATUS_TONE: Record<SubscriptionStatus, NonNullable<BadgeProps["tone"]>> = {
  ACTIVE: "success",
  PENDING: "accent",
  EXPIRED: "neutral",
  CANCELLED: "danger",
};

const PAYMENT_STATUS_TONE: Record<PaymentStatus, NonNullable<BadgeProps["tone"]>> = {
  VERIFIED: "success",
  INITIATED: "neutral",
  PENDING: "accent",
  FAILED: "danger",
  REFUNDED: "accent",
};

function classLabel(classId: string): string {
  const n = classId.replace(/^class-/, "");
  return /^\d+$/.test(n) ? `Class ${n}` : classId;
}

function planLabel(plan: string): string {
  return plan === "yearly" ? "Annual Plan" : plan;
}

/**
 * Subscription + payment-history content, shared by the Settings "Subscription
 * & Billing" section and the standalone /billing route. The upgrade/checkout
 * flow still lives on the full /subscribe route -- the "See Annual Plan" CTA here
 * navigates there.
 */
export function BillingPanel() {
  const navigate = useNavigate();
  const mySubs = useMySubscriptions();
  const payments = usePayments();

  const active = mySubs.data?.active ?? null;

  return (
    <div className="flex flex-col gap-10">
      {/* Current subscription ------------------------------------------- */}
      <section>
        <h2 className="text-sm font-medium text-text-muted mb-3">Current subscription</h2>
        {mySubs.isLoading ? (
          <Card className="p-6">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="mt-3 h-4 w-56" />
          </Card>
        ) : mySubs.isError ? (
          <Card className="p-6">
            <p className="text-sm text-text-muted">Couldn't load your subscription.</p>
          </Card>
        ) : active ? (
          <ActiveSubscriptionCard sub={active} />
        ) : (
          <NoSubscriptionCard onSubscribe={() => navigate("/subscribe")} />
        )}
      </section>

      {/* Payment history ------------------------------------------------ */}
      <section>
        <h2 className="text-sm font-medium text-text-muted mb-3">Payment history</h2>
        {payments.isLoading ? (
          <Card className="divide-y divide-border">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between p-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-5 w-16" />
              </div>
            ))}
          </Card>
        ) : payments.isError ? (
          <Card className="p-6">
            <p className="text-sm text-text-muted">Couldn't load your payments.</p>
          </Card>
        ) : (payments.data ?? []).length === 0 ? (
          <Card className="p-8 text-center">
            <ReceiptText className="mx-auto h-8 w-8 text-text-subtle" />
            <p className="mt-3 text-sm text-text-muted">No payments yet.</p>
          </Card>
        ) : (
          <Card className="divide-y divide-border">
            {(payments.data ?? []).map((p) => (
              <PaymentRow key={p.payment_id} payment={p} />
            ))}
          </Card>
        )}
      </section>
    </div>
  );
}

function ActiveSubscriptionCard({ sub }: { sub: SubscriptionRecord }) {
  const navigate = useNavigate();
  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-success" />
            <span className="font-display text-lg font-semibold">
              {classLabel(sub.class)}
            </span>
            <Badge tone={SUB_STATUS_TONE[sub.status]}>{sub.status}</Badge>
          </div>
          <p className="mt-1 text-sm text-text-muted">
            {planLabel(sub.plan)} plan - {formatRupees(sub.amount_paid / 100)}
          </p>
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-4 text-sm">
        <div>
          <dt className="text-text-subtle">Started</dt>
          <dd className="mt-0.5 font-medium">{formatDate(sub.start_date)}</dd>
        </div>
        <div>
          <dt className="text-text-subtle">Access valid until</dt>
          <dd className="mt-0.5 font-medium">{formatDate(sub.end_date)}</dd>
        </div>
      </dl>

      <div className="mt-6 flex flex-col sm:flex-row gap-2">
        <Button onClick={() => navigate("/subjects")}>Start studying</Button>
      </div>
    </Card>
  );
}

function NoSubscriptionCard({ onSubscribe }: { onSubscribe: () => void }) {
  return (
    <Card className="p-6 sm:p-8 text-center">
      <p className="font-display text-lg font-semibold">No active subscription</p>
      <p className="mt-2 text-sm text-text-muted">
        Subscribe to unlock every chapter and study mode for your class.
      </p>
      <Button className="mt-5" onClick={onSubscribe}>
        See Annual Plan
      </Button>
    </Card>
  );
}

function PaymentRow({ payment }: { payment: PaymentRecord }) {
  return (
    <div className="flex items-center justify-between gap-3 p-4">
      <div className="min-w-0">
        <p className="font-medium">{formatRupees(payment.amount / 100)}</p>
        <p className="text-xs text-text-muted">
          {formatDate(payment.created_at)}
          {payment.payment_method ? ` - ${payment.payment_method}` : ""}
        </p>
      </div>
      <Badge tone={PAYMENT_STATUS_TONE[payment.status]}>{payment.status}</Badge>
    </div>
  );
}
